import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Edit2, AlertTriangle, Tag, Link2,
  MessageSquare, Lock, Send, Paperclip,
  BellOff, BellRing, X, Plus, Mail, Sparkles, ChevronDown,
  Download, Share2, Eye, UserCheck, FileText, Bot, Pencil, Clock, Check, ClipboardCheck,
} from 'lucide-react';
import { ticketsApi, type TicketDraft, type TicketDraftInput } from '../api/tickets';
import { useTicketDraft, type UseTicketDraftResult } from '../hooks/useTicketDraft';
import { describeDrafters } from '../components/tickets/TicketRowMarks';
import { aiApi } from '../api/ai';
import { teamsApi } from '../api/teams';
import { detectPhiInTitle } from '../lib/phiHeuristics';
import { primaryRecipient, ccRecipient } from '../lib/recipients';
import { usePermissions } from '../contexts/PermissionsContext';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import EmptyState from '../components/EmptyState';
import { StatusBadge, PriorityBadge } from '../components/badges';
import { useConfirm } from '../components/ConfirmDialog';
import TimeEntryForm from '../components/tickets/TimeEntryForm';
import TimeEntryList from '../components/tickets/TimeEntryList';
import ActivityLog from '../components/tickets/ActivityLog';
import AIPanel from '../components/tickets/AIPanel';
import ReplyAIAssist from '../components/tickets/ReplyAIAssist';
import SentimentBadge from '../components/tickets/SentimentBadge';
import TimerControls from '../components/tickets/TimerControls';
import TicketEditModal from '../components/tickets/TicketEditModal';
import LinkTicketsModal from '../components/tickets/LinkTicketsModal';
import ShareTicketModal from '../components/tickets/ShareTicketModal';
import AIPromptModal from '../components/tickets/AIPromptModal';
import SendToClaudeModal from '../components/tickets/SendToClaudeModal';
import CopyableEmail from '../components/tickets/CopyableEmail';
import StatusPicker from '../components/tickets/StatusPicker';
import QuickAssign from '../components/tickets/QuickAssign';
import TaskList from '../components/tickets/TaskList';
import CollaboratorsPanel from '../components/tickets/CollaboratorsPanel';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import FileUploader from '../components/FileUploader';
import MessageContent from '../components/MessageContent';
import { MessageCollapseToggle, MessagePreviewLine, ExpandCollapseAll } from '../components/MessageCollapse';
import { messageId, messagePreview, isCollapsible, newestFirst } from '../lib/messageThread';
import { useMessageCollapse } from '../hooks/useMessageCollapse';
import {
  insertUploadingPlaceholder,
  resolveUploadingPlaceholder,
  hasPendingInlineUploads,
  unreferencedAttachments,
  collectGalleryImages,
  isImageAttachment,
} from '../lib/inlineImages';
import { ImageLightboxProvider, useImageLightbox } from '../components/ImageLightbox';
import { useTicketStatuses } from '../hooks/useTicketStatuses';
import { useClassifications } from '../hooks/useClassifications';
import type { UploadedAttachment } from '../api/attachments';
import type { Ticket, TicketStatus, TicketStatusDef, TicketPriority } from '../types';

// ─── helpers ──────────────────────────────────────────────────────────────

function fmtDateTime(d?: string | null) {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ' at '
    + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function fmtDate(d?: string | null) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "just now", "5 minutes ago", "2 hours ago", "yesterday", else a date. */
function agoInWords(d?: string | Date | null): string {
  if (!d) return '';
  const then = new Date(d).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return fmtDate(new Date(then).toISOString());
}

function initials(name?: string | null) {
  if (!name) return '?';
  return name.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
}

function fmtSize(bytes?: number | null) {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Types the browser can display in a tab (mirrors the backend's
// INLINE_SAFE_MIME — everything else is served as a download anyway).
const VIEWABLE_EXT = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'pdf', 'txt', 'md', 'markdown']);

function isViewable(filename?: string | null): boolean {
  const ext = (filename ?? '').split('.').pop()?.toLowerCase() ?? '';
  return VIEWABLE_EXT.has(ext);
}

function downloadUrlOf(url: string): string {
  return url.includes('?') ? `${url}&download=1` : `${url}?download=1`;
}

/** Chips for message- or ticket-level attachments: view (when the browser
    can render the type) and download actions. Image attachments open in the
    in-app lightbox pop-up; other viewable types still open in a tab. */
function AttachmentChips({ attachments }: { attachments?: Array<Record<string, any>> }) {
  const lightbox = useImageLightbox();
  if (!Array.isArray(attachments) || attachments.length === 0) return null;
  return (
    <ul className="mt-2 flex flex-wrap gap-2">
      {attachments.map((att) => {
        const viewable = isViewable(att.filename);
        const dlUrl = downloadUrlOf(att.url);
        const popup = !!lightbox && isImageAttachment(att as any);
        const openPopup = () => lightbox!.openImage(att.url, att.filename);
        const nameChip = (
          <>
            <Paperclip className="w-3 h-3 text-muted shrink-0" />
            <span className="truncate max-w-[14rem]">{att.filename}</span>
            {fmtSize(att.size) && <span className="text-muted shrink-0">{fmtSize(att.size)}</span>}
          </>
        );
        return (
          <li
            key={att.id ?? att.url}
            className="inline-flex items-stretch rounded-md bg-row-odd border border-line-light text-xs overflow-hidden"
          >
            {popup ? (
              <button
                type="button"
                onClick={openPopup}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-fg hover:text-primary transition-colors"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                title={`View ${att.filename}`}
              >
                {nameChip}
              </button>
            ) : (
              <a
                href={viewable ? att.url : dlUrl}
                target={viewable ? '_blank' : undefined}
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-2 py-1 text-fg hover:text-primary transition-colors"
                title={viewable ? `View ${att.filename}` : `Download ${att.filename}`}
              >
                {nameChip}
              </a>
            )}
            {viewable && (popup ? (
              <button
                type="button"
                onClick={openPopup}
                className="inline-flex items-center px-1.5 border-l border-line-light text-muted hover:text-primary transition-colors"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                title="View"
              >
                <Eye className="w-3.5 h-3.5" />
              </button>
            ) : (
              <a
                href={att.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center px-1.5 border-l border-line-light text-muted hover:text-primary transition-colors"
                title="View in browser"
              >
                <Eye className="w-3.5 h-3.5" />
              </a>
            ))}
            <a
              href={dlUrl}
              className="inline-flex items-center px-1.5 border-l border-line-light text-muted hover:text-primary transition-colors"
              title="Download"
            >
              <Download className="w-3.5 h-3.5" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

// ─── ChangeStatusModal ────────────────────────────────────────────────────
// Statuses come from the company's own set. Moving into a resolved-category
// status requires a resolution entry; closing (closed category) an unresolved
// ticket requires a reason explaining why it's closed rather than resolved.
//
// The picker applies everything else in one click, so this dialog only opens
// for the transitions that need text — pre-selected on the chosen status.

function ChangeStatusModal({
  open,
  onClose,
  statuses,
  currentStatus,
  currentResolution,
  initialStatus,
  pending,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  statuses: TicketStatusDef[];
  currentStatus: TicketStatus;
  currentResolution?: string;
  /** Status chosen in the picker — seeds the dropdown when the dialog opens. */
  initialStatus?: TicketStatus | null;
  pending: boolean;
  onSubmit: (status: TicketStatus, extra: { resolution?: string; closedReason?: string }) => void;
}) {
  const [next, setNext] = useState<TicketStatus | ''>('');
  const [resolution, setResolution] = useState('');
  const [closedReason, setClosedReason] = useState('');

  // Seed on open only — a refetch that lands while the dialog is up must not
  // overwrite the resolution the user is typing.
  useEffect(() => {
    if (open) {
      setNext(initialStatus ?? '');
      setResolution(currentResolution ?? '');
      setClosedReason('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = statuses.filter(s => s.isActive && s.key !== currentStatus);
  const currentCategory = statuses.find(s => s.key === currentStatus)?.category;
  const nextCategory = statuses.find(s => s.key === next)?.category;
  const needsResolution = nextCategory === 'resolved';
  const needsClosedReason = nextCategory === 'closed' && currentCategory !== 'resolved';
  const canSubmit = !!next
    && (!needsResolution || resolution.trim().length > 0)
    && (!needsClosedReason || closedReason.trim().length > 0);

  const submit = () => {
    if (!canSubmit || !next) return;
    onSubmit(next, {
      ...(needsResolution && { resolution: resolution.trim() }),
      ...(nextCategory === 'closed' && closedReason.trim() && { closedReason: closedReason.trim() }),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change status"
      busy={pending}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!canSubmit || pending}>
            {pending ? 'Updating…' : 'Update status'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-muted flex items-center gap-2">
          Current status: <StatusBadge status={currentStatus} />
        </p>

        <div>
          <label className="label">New status</label>
          <select
            className="select w-full"
            value={next}
            onChange={(e) => setNext(e.target.value as TicketStatus | '')}
            autoFocus={!initialStatus}
          >
            <option value="">Select a status…</option>
            {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </div>

        {needsResolution && (
          <div>
            <label className="label">Resolution <span className="text-err">*</span></label>
            <textarea
              className="input"
              rows={4}
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
              placeholder="Describe how this ticket was resolved…"
              autoFocus={!!initialStatus}
            />
            <p className="text-xs text-muted mt-1">
              Saved to the ticket's Resolution section and recorded in the activity log.
            </p>
          </div>
        )}

        {nextCategory === 'closed' && (
          <div>
            <label className="label">
              {needsClosedReason
                ? <>Reason for closing <span className="text-err">*</span></>
                : 'Closing note (optional)'}
            </label>
            <textarea
              className="input"
              rows={4}
              value={closedReason}
              onChange={(e) => setClosedReason(e.target.value)}
              placeholder={needsClosedReason
                ? 'This ticket was not resolved — explain why it is being closed…'
                : 'Optional note about closing this ticket…'}
            />
            {needsClosedReason && (
              <p className="text-xs text-muted mt-1">
                Closing skips resolution — say why (e.g. duplicate, spam, no response, won't fix).
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── PhiTitleWarning ──────────────────────────────────────────────────────
// Shown to staff when the title trips the local PHI heuristics. Detection is
// pure regex (free, every view); AI is only called when the user explicitly
// clicks "Suggest with AI" — kept sparing by design.

function PhiTitleWarning({
  ticketId,
  title,
  findings,
  canEdit,
}: {
  ticketId: string;
  title: string;
  findings: string[];
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [aiLoading, setAiLoading] = useState(false);

  const saveMutation = useMutation({
    mutationFn: (newTitle: string) => ticketsApi.update(ticketId, { title: newTitle }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Title updated');
      setEditing(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const suggestWithAi = async () => {
    setAiLoading(true);
    try {
      const suggestion = await aiApi.sanitizeTitle(ticketId);
      if (suggestion?.trim()) setDraft(suggestion.trim());
      else toast.error('No suggestion returned');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? e.message ?? 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div
      className="card"
      style={{ borderLeft: '3px solid var(--scpa-status-warning)', padding: '0.75rem 1rem' }}
    >
      <div className="flex items-start gap-2.5">
        <AlertTriangle
          className="w-4 h-4 shrink-0 mt-0.5"
          style={{ color: 'var(--scpa-status-warning)' }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-fg">
            This title may contain PHI ({findings.join(', ')}).
          </p>
          <p className="text-xs text-muted mt-0.5">
            Titles appear in ticket lists and can appear in outbound email. Consider
            rewording so nothing identifies a person or their health information.
          </p>

          {editing && (
            <div className="mt-3 space-y-2">
              <input
                className="input w-full"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                maxLength={500}
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn btn-primary text-xs py-1.5"
                  onClick={() => saveMutation.mutate(draft.trim())}
                  disabled={!draft.trim() || draft.trim() === title || saveMutation.isPending}
                >
                  {saveMutation.isPending ? 'Saving…' : 'Save title'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary text-xs py-1.5 inline-flex items-center gap-1.5"
                  onClick={suggestWithAi}
                  disabled={aiLoading}
                  title="Ask the configured AI to suggest a PHI-free rewrite"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  {aiLoading ? 'Suggesting…' : 'Suggest with AI'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost text-xs py-1.5"
                  onClick={() => { setEditing(false); setDraft(title); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {canEdit && !editing && (
          <button
            type="button"
            className="btn btn-secondary text-xs py-1.5 shrink-0"
            onClick={() => { setDraft(title); setEditing(true); }}
          >
            Fix title
          </button>
        )}
      </div>
    </div>
  );
}

// ─── MessageBubble ────────────────────────────────────────────────────────

function MessageBubble({ msg, expanded = true, onToggle }: {
  msg: any;
  /** Collapsed messages show a one-line preview under the header. */
  expanded?: boolean;
  /** Omitted for a message short enough that there's nothing to hide. */
  onToggle?: () => void;
}) {
  const isInternal = msg.type === 'internal_note';
  // Task notes: progress on a sub-ticket task — visible to everyone on the
  // ticket (unlike internal notes), tagged with the task they belong to.
  const isTaskNote = msg.type === 'task_note';
  const authorName = msg.authorName ?? msg.author?.name ?? msg.authorId ?? 'Unknown';
  const ini = initials(authorName);
  const collapsed = !!onToggle && !expanded;

  return (
    <div
      className={`flex gap-3 px-5 ${collapsed ? 'py-2.5 cursor-pointer' : 'py-4'} ${isInternal || isTaskNote ? 'border-l-2' : 'hover:bg-row-odd'}`}
      onClick={collapsed ? onToggle : undefined}
      style={isInternal
        ? { background: 'var(--scpa-primary-muted)', borderColor: 'var(--scpa-status-warning)' }
        : isTaskNote ? { borderColor: 'var(--scpa-primary)' } : undefined}
    >
      {/* Avatar */}
      <div
        className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-semibold ${isInternal ? '' : 'brand-gradient text-white'}`}
        style={isInternal ? { background: 'var(--scpa-status-warning)', color: '#fff' } : undefined}
      >
        {ini}
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        <div className={`flex items-center gap-2 flex-wrap ${collapsed ? 'mb-0.5' : 'mb-1.5'}`}>
          {onToggle && <MessageCollapseToggle expanded={expanded} onToggle={onToggle} label={authorName} />}
          <span className="text-sm font-semibold text-fg">
            {authorName}
          </span>
          {isInternal ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-warn">
              <Lock className="w-2.5 h-2.5" /> Internal note
            </span>
          ) : isTaskNote ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary" title={msg.task?.title}>
              <ClipboardCheck className="w-2.5 h-2.5" /> Task note{msg.task?.title ? ` · ${msg.task.title}` : ''}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary">
              <MessageSquare className="w-2.5 h-2.5" /> Reply
            </span>
          )}
          <span className="text-xs text-muted ml-auto">
            {fmtDateTime(msg.createdAt)}
          </span>
        </div>
        {collapsed ? (
          <MessagePreviewLine
            text={messagePreview(msg.content).text}
            attachmentCount={(msg.attachments ?? []).length}
          />
        ) : (
          <>
            <MessageContent content={String(msg.content ?? '')} />
            {/* Chips skip images already shown inline, so screenshots aren't listed twice. */}
            <AttachmentChips attachments={unreferencedAttachments(String(msg.content ?? ''), (msg as any).attachments ?? [])} />
            {msg.isEdited && (
              <p className="text-xs text-muted mt-1 italic">edited</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── PropRow ──────────────────────────────────────────────────────────────

function PropRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value || value === '—' || value === '-') return null;
  return (
    <div className="flex items-start justify-between gap-2 py-1.5 border-b border-line-light last:border-0">
      <dt className="text-xs text-muted shrink-0">{label}</dt>
      <dd className="text-xs text-right text-fg font-medium">{value}</dd>
    </div>
  );
}

// ─── SidebarSection ───────────────────────────────────────────────────────

function SidebarSection({ title, children }: { title: string; children: React.ReactNode }) {
  // Below lg the sidebar stacks under the conversation, so sections start
  // collapsed there to keep the page short; on desktop they're always open.
  const [open, setOpen] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  return (
    <div className="rounded-xl border border-line bg-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left lg:pointer-events-none ${open ? 'border-b border-line-light' : 'lg:border-b lg:border-line-light'}`}
        aria-expanded={open}
      >
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">{title}</p>
        <ChevronDown className={`w-3.5 h-3.5 text-muted transition-transform lg:hidden ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`p-4 ${open ? '' : 'hidden lg:block'}`}>{children}</div>
    </div>
  );
}

// ─── TicketDetail ─────────────────────────────────────────────────────────

export default function TicketDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { can, isStaff } = usePermissions();
  const { user } = useAuth();
  const { currentCompany } = useCompany();
  const myId: string | null = (user as any)?.id ?? (user as any)?._id ?? null;
  const companyId: string | undefined = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const confirm = useConfirm();
  const [composeTab, setComposeTab] = useState<'reply' | 'note'>('reply');
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<UploadedAttachment[]>([]);
  const [showAttachPanel, setShowAttachPanel] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  /** Linked tickets this reply/note should also be posted to. */
  const [crossPostIds, setCrossPostIds] = useState<string[]>([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  /** Status picked in the header when it needs a resolution/reason first. */
  const [pendingStatus, setPendingStatus] = useState<TicketStatus | null>(null);
  /** Status staged in the compose box — applied when the message is sent. */
  const [composeStatus, setComposeStatus] = useState<TicketStatus | null>(null);
  /** Reassignment staged in the compose box — applied with the message, so
   *  the reply, status and new assignee go out as one email per person. */
  const [composeAssignee, setComposeAssignee] = useState<{ id: string | null; name: string | null } | undefined>(undefined);
  /** Per-update opt-out: when the ticket allows update text in emails
   *  (emailIncludeContent), uncheck this to keep one update link-only
   *  (e.g. it happens to carry PHI). Resets to checked after each send. */
  const [includeUpdateContent, setIncludeUpdateContent] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [showSendToClaude, setShowSendToClaude] = useState(false);
  const [showAIPromptModal, setShowAIPromptModal] = useState(false);
  const [ccEmailInput, setCcEmailInput] = useState('');
  const [ccNameInput, setCcNameInput] = useState('');
  /** Scopes paste-to-attach to the compose box. */
  const composeRef = useRef<HTMLFormElement>(null);
  /** For inserting inline-image references at the caret. */
  const replyTextareaRef = useRef<HTMLTextAreaElement>(null);
  /** The draft hook is wired below the send mutation (it pauses while a send
   *  is in flight); the mutation's success handler reaches it through here. */
  const draftApiRef = useRef<UseTicketDraftResult | null>(null);

  const { data: ticket, isLoading, error } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsApi.get(id!),
    enabled: !!id,
  });

  const { data: messages = [] } = useQuery({
    queryKey: ['ticket-messages', id],
    queryFn: () => ticketsApi.getMessages(id!),
    enabled: !!id,
  });

  // The conversation reads newest first (cTKT-00295); the API stays oldest-first.
  const thread = useMemo(() => newestFirst(messages as any[]), [messages]);
  const collapse = useMessageCollapse(thread.map(messageId), id);

  // Every image the page displays, in display order — the lightbox pop-up's
  // arrows walk this set.
  const galleryImages = useMemo(() => collectGalleryImages([
    { content: String(ticket?.description ?? ''), attachments: ((ticket as any)?.attachments ?? []) },
    ...thread.map((m: any) => ({ content: String(m.content ?? ''), attachments: m.attachments ?? [] })),
  ]), [ticket, thread]);

  // Teams (with members) — so the assigned team can show who's on it.
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsApi.list(),
  });

  // Company's custom status set (also feeds the badge registry).
  const { statuses: statusDefs } = useTicketStatuses();
  // The company's own label for the ticket's type — it may have been renamed
  // or added by them, so never print the stored key.
  const { labelOf: classificationLabel } = useClassifications();
  const typeLabel = (v?: string | null) => classificationLabel('type', v) || '—';

  // Status/reply changes affect the list, Home panels, and stat boxes too —
  // invalidate them all so navigating back never shows stale counts.
  const invalidateTicketQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    queryClient.invalidateQueries({ queryKey: ['tickets'] });
    queryClient.invalidateQueries({ queryKey: ['ticket-stats'] });
    queryClient.invalidateQueries({ queryKey: ['home-my-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['home-aged-tickets'] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ status, extra }: { status: TicketStatus; extra?: { resolution?: string; closedReason?: string } }) =>
      ticketsApi.updateStatus(id!, status, extra),
    onSuccess: () => {
      invalidateTicketQueries();
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', id] });
      setShowStatusModal(false);
      setPendingStatus(null);
      toast.success('Status updated');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const messageMutation = useMutation({
    mutationFn: ({ content, type, attachments, status, includeContent, linkedTicketIds, assignedToId }: { content: string; type: 'reply' | 'internal_note'; attachments?: UploadedAttachment[]; status?: TicketStatus; includeContent?: boolean; linkedTicketIds?: string[]; assignedToId?: string | null }) =>
      ticketsApi.createMessage(id!, { content, type, attachments, status, includeContent, linkedTicketIds, assignedToId }),
    onSuccess: (data, vars) => {
      // The server dropped the autosaved draft along with the send; retire
      // it locally before the box empties so nothing tries to re-save.
      draftApiRef.current?.markSent();
      setReplyContent('');
      setReplyAttachments([]);
      setShowAttachPanel(false);
      setComposeStatus(null);
      setComposeAssignee(undefined);
      setIncludeUpdateContent(true);
      setCrossPostIds([]);
      queryClient.invalidateQueries({ queryKey: ['ticket-messages', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', id] });
      invalidateTicketQueries();
      let sent = vars.type === 'internal_note' ? 'Note added' : 'Reply sent';
      if (data?.crossPosted?.length) {
        sent += ` — also posted to ${data.crossPosted.map(c => c.ticketNumber).join(', ')}`;
      }
      if (data?.status) sent += ` — status now ${data.status.label}`;
      if (data?.assignedTo !== undefined) {
        sent += data.assignedTo ? ` — assigned to ${data.assignedTo.name}` : ' — unassigned';
      }
      toast.success(sent);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unlinkMutation = useMutation({
    mutationFn: (relatedId: string) => ticketsApi.removeRelated(id!, relatedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket-activity', id] });
      toast.success('Ticket unlinked');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const notifyMutation = useMutation({
    mutationFn: (notify: boolean) => ticketsApi.update(id!, { notifyCustomer: notify } as any),
    onSuccess: (_, notify) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success(notify ? 'Customer notifications enabled' : 'Customer notifications muted');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const creatorNotifyMutation = useMutation({
    mutationFn: (notify: boolean) => ticketsApi.update(id!, { notifyCreator: notify } as any),
    onSuccess: (_, notify) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success(notify ? 'Creator will be notified of updates' : 'Creator notifications off');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const includeContentMutation = useMutation({
    mutationFn: (on: boolean) => ticketsApi.update(id!, { emailIncludeContent: on } as any),
    onSuccess: (_, on) => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success(on ? 'Update text will be included in emails' : 'Emails back to link-only');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const addCcMutation = useMutation({
    mutationFn: (data: { email: string; name?: string }) => ticketsApi.addCc(id!, data),
    onSuccess: () => {
      setCcEmailInput('');
      setCcNameInput('');
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      toast.success('CC added');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const removeCcMutation = useMutation({
    mutationFn: (ccId: string) => ticketsApi.removeCc(id!, ccId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Compose-box autosave. The box's live values go in; a saved draft comes
  // back through onRestore when the ticket opens. Restoring is keyed on the
  // ticket id inside the hook (never on the fetched ticket object), per the
  // no-cache rule: a background refetch must not touch what's being typed.
  const draftValues: TicketDraftInput = {
    type: composeTab === 'note' ? 'internal_note' : 'reply',
    content: replyContent,
    attachments: replyAttachments,
    status: composeStatus,
  };
  const draft = useTicketDraft(id, draftValues, {
    enabled: !!id,
    paused: messageMutation.isPending,
    onRestore: (d: TicketDraft) => {
      setComposeTab(d.type === 'internal_note' ? 'note' : 'reply');
      setReplyContent(d.content);
      setReplyAttachments(d.attachments ?? []);
      if (d.attachments?.length) setShowAttachPanel(true);
      setComposeStatus((d.status as TicketStatus | null) ?? null);
    },
  });
  draftApiRef.current = draft;

  const discardDraft = async () => {
    const ok = await confirm({
      title: 'Discard draft?',
      message: 'Your unsent text and any attached files on it will be removed.',
      confirmLabel: 'Discard',
      danger: true,
    });
    if (!ok) return;
    await draft.discard();
    setReplyContent('');
    setReplyAttachments([]);
    setShowAttachPanel(false);
    setComposeStatus(null);
  };

  if (!id) return <div className="text-err">Invalid ticket ID</div>;
  if (isLoading) return <div className="py-20 text-center text-muted">Loading ticket…</div>;
  if (error || !ticket) {
    return (
      <div className="py-20 text-center text-err">
        {(error as Error)?.message ?? 'Ticket not found'}
      </div>
    );
  }

  const t = ticket as any;
  const ticketId = t.id ?? t._id ?? id;
  // Who customer emails actually go TO — CRM contact, then contact, then the
  // raw reporter fields. Cheap enough to recompute; no hooks allowed below
  // the early returns above.
  const primaryTo = primaryRecipient(t);
  const status = t.status as TicketStatus;
  const showStaffFeatures = isStaff && can('viewInternal', 'tickets');
  const canChangeStatus = can('edit', 'tickets');
  const currentCategory = statusDefs.find(s => s.key === status)?.category;
  // A plain reply doesn't touch the ticket record, so updatedAt alone can lag
  // behind the conversation — fold message times in. ISO strings sort correctly.
  const lastActivityAt = [t.updatedAt, ...(messages as any[]).map((m: any) => m.createdAt)]
    .filter((d): d is string => !!d && !Number.isNaN(new Date(d).getTime()))
    .sort()
    .pop() ?? null;
  const composeCategory = composeStatus
    ? statusDefs.find(s => s.key === composeStatus)?.category
    : undefined;
  const currentAssigneeId: string | null = t.assignedToId ?? t.assignedTo?.id ?? null;
  const canAssign = showStaffFeatures && can('assign', 'tickets');
  const assigneeStaged = !!composeAssignee && composeAssignee.id !== currentAssigneeId;
  // Local heuristics only — no AI unless the user asks for a rewrite.
  const phiFindings = showStaffFeatures ? detectPhiInTitle(ticket.title) : [];

  // Relation-linked tickets (either direction, deduped) — drives the related
  // list and the "also send to linked tickets" cross-post picker.
  const linkedTickets: Array<{ id: string; ticketNumber: string; title: string; status: string; relationType: string }> = (() => {
    const seen = new Set<string>();
    const out: Array<{ id: string; ticketNumber: string; title: string; status: string; relationType: string }> = [];
    for (const r of [...(t.relatedFrom ?? []), ...(t.relatedTo ?? [])]) {
      const other = r.toTicket ?? r.fromTicket;
      if (!other || seen.has(other.id)) continue;
      seen.add(other.id);
      out.push({ ...other, relationType: r.relationType ?? 'related' });
    }
    return out;
  })();
  const toggleCrossPost = (ticketIdToToggle: string) =>
    setCrossPostIds(prev => (prev.includes(ticketIdToToggle)
      ? prev.filter(x => x !== ticketIdToToggle)
      : [...prev, ticketIdToToggle]));

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const blob = await ticketsApi.exportPdf(ticketId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.ticketNumber || 'ticket'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // One click opens the list; the pick applies straight away unless the target
  // needs text first (a resolution, or a reason for closing something that was
  // never resolved), in which case the dialog opens pre-selected on it.
  const handleStatusPick = (next: TicketStatus) => {
    const target = statusDefs.find(s => s.key === next);
    const needsText = target?.category === 'resolved'
      || (target?.category === 'closed' && currentCategory !== 'resolved');
    if (needsText) {
      setPendingStatus(next);
      setShowStatusModal(true);
      return;
    }
    statusMutation.mutate({ status: next });
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setPendingStatus(null);
  };

  // Pasted screenshot: drop an "Uploading…" placeholder at the caret so the
  // image lands inline where the user was typing, then swap in the real
  // ![name](url) reference when the upload finishes (or remove it on failure).
  // The file still joins replyAttachments via the uploader's normal onChange —
  // that row is what drives access control on the file.
  const handlePasteImageStart = (id: string, file: File) => {
    setReplyContent(insertUploadingPlaceholder(replyTextareaRef.current, replyContent, id, file.name));
  };

  const handlePasteImageDone = (id: string, att: UploadedAttachment | null) => {
    setReplyContent(resolveUploadingPlaceholder(replyTextareaRef.current, replyContent, id, att));
  };

  const inlineUploadPending = hasPendingInlineUploads(replyContent);

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || inlineUploadPending) return;
    // Only currently-linked tickets are valid cross-post targets (a link may
    // have been removed since the boxes were checked).
    const crossPostTargets = crossPostIds.filter(cid => linkedTickets.some(lt => lt.id === cid));
    messageMutation.mutate({
      content: replyContent.trim(),
      type: composeTab === 'note' ? 'internal_note' : 'reply',
      attachments: replyAttachments,
      // Only meaningful when the ticket's emailIncludeContent is on — the
      // backend ignores it otherwise.
      includeContent: includeUpdateContent,
      ...(crossPostTargets.length > 0 && { linkedTicketIds: crossPostTargets }),
      // The message body becomes the resolution / closing reason the target
      // status requires — which is why an update is mandatory here.
      ...(composeStatus && composeStatus !== status && { status: composeStatus }),
      ...(composeAssignee && composeAssignee.id !== currentAssigneeId && { assignedToId: composeAssignee.id }),
    });
  };

  // Switching reply<->internal-note must not silently carry a draft across
  // channels (risk: posting an internal note as a public reply). Confirm
  // before discarding an in-progress draft, then clear it — on the server
  // too, so it isn't offered back under the other tab.
  const switchComposeTab = async (next: 'reply' | 'note') => {
    if (next === composeTab) return;
    if (replyContent.trim()) {
      const ok = await confirm({
        title: 'Discard draft?',
        message: 'Switching will discard your current draft.',
        confirmLabel: 'Discard',
        danger: true,
      });
      if (!ok) return;
      await draft.discard();
    }
    setReplyContent('');
    setComposeTab(next);
  };

  const currentStatusLabel = statusDefs.find(s => s.key === status)?.label ?? String(status).replace(/_/g, ' ');

  return (
    <ImageLightboxProvider images={galleryImages}>
    <div className="flex flex-col gap-4 pb-10">

      <PageHeader
        title={ticket.title}
        intro={
          <span className="flex items-center gap-2 flex-wrap">
            <span className="font-mono">{t.ticketNumber}</span>
            {(t.slaResponseBreached || t.slaResolutionBreached) && (
              <span className="flex items-center gap-1 text-err font-medium">
                <AlertTriangle className="w-3.5 h-3.5" /> SLA breached
              </span>
            )}
          </span>
        }
        actions={
          <>
            {canChangeStatus ? (
              <StatusPicker
                statuses={statusDefs}
                current={status}
                onSelect={handleStatusPick}
                disabled={statusMutation.isPending}
              />
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm px-1">
                <span className="text-muted font-medium">Status:</span>
                <StatusBadge status={status} />
              </span>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              title="Export ticket as PDF"
            >
              <Download className="w-3.5 h-3.5" /> {exportingPdf ? 'Exporting…' : 'PDF'}
            </button>

            {showStaffFeatures && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowAIPromptModal(true)}
                title="Format this ticket as a prompt for an AI coding assistant"
              >
                <Sparkles className="w-3.5 h-3.5" /> AI Prompt
              </button>
            )}

            {showStaffFeatures && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowSendToClaude(true)}
                title="Send this ticket (prompt + attachments) to Claude Code on your dev machine"
              >
                <Bot className="w-3.5 h-3.5" /> Send to Claude
              </button>
            )}

            {showStaffFeatures && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowShareModal(true)}
                title="Share ticket via email"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
            )}

            {showStaffFeatures && can('edit', 'tickets') && (
              <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(true)}>
                <Edit2 className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            {showStaffFeatures && <TimerControls ticketId={ticketId} />}
          </>
        }
      />

      {phiFindings.length > 0 && (
        <PhiTitleWarning
          ticketId={ticketId}
          title={ticket.title}
          findings={phiFindings}
          canEdit={can('edit', 'tickets')}
        />
      )}

      {/* Waiting on the viewer: the status is waiting-on-customer and they are
          the creator / contact / reporter / CC. Same email they were sent,
          said in the app. */}
      {t.waitingOnYou && (
        <div
          className="rounded-xl border px-4 py-3 flex items-start gap-3"
          style={{ borderColor: 'var(--scpa-status-warning)', background: 'var(--scpa-primary-muted)' }}
        >
          <Clock className="w-5 h-5 shrink-0 mt-0.5" style={{ color: 'var(--scpa-status-warning)' }} />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-fg">This ticket is waiting on you</p>
            <p className="text-xs text-muted mt-0.5 leading-relaxed">
              It is in <b className="text-fg">{currentStatusLabel}</b>: the team needs a reply, a test, or a
              confirmation from you before it can move on. Reply in the conversation below when you're ready.
            </p>
          </div>
        </div>
      )}

      {/* ── Two-column body — stacks below lg (sidebar under the thread) ── */}
      <div className="flex flex-col lg:flex-row gap-4">

        {/* ── LEFT: main content ──────────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-5">

          {/* Description card */}
          <div className="card overflow-hidden" style={{ padding: 0 }}>
            <div className="px-5 py-4 border-b border-line-light flex items-center gap-3">
              <h2 className="section-title text-base">Description</h2>
              {/* Tone of the description itself — the title is a label, not
                  something the requester "said". */}
              {showStaffFeatures && (
                <SentimentBadge text={String(ticket.description ?? '')} />
              )}
            </div>

            <div className="px-5 py-4">
              <MessageContent content={String(ticket.description ?? '')} />

              {/* Ticket-level attachments (added at creation) — images already
                  shown inline in the description aren't listed again. */}
              {(() => {
                const chips = unreferencedAttachments(String(ticket.description ?? ''), (t.attachments ?? []) as any[]);
                if (chips.length === 0) return null;
                return (
                  <div className="mt-4">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">
                      Attachments ({chips.length})
                    </p>
                    <AttachmentChips attachments={chips} />
                  </div>
                );
              })()}

              {/* Metadata grid */}
              <div className="mt-5 pt-5 border-t border-line-light grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-x-4 gap-y-3 text-sm">
                {[
                  ['Type',         typeLabel(t.type)],
                  ['Source',       String(t.source ?? 'portal').replace(/_/g, ' ')],
                  ['Product line', (t as any).productLine?.name ?? null],
                  ['Impact',       t.impact ? String(t.impact).replace(/_/g, ' ') : null],
                  ['Environment',  t.environment ? String(t.environment).replace(/_/g, ' ') : null],
                  ['Product',      t.product ?? null],
                  ['Version',      t.productVersion ?? null],
                  ['Department',   t.department ?? null],
                  ['Location',     t.location ?? null],
                  ['Due date',     t.dueDate ? fmtDate(t.dueDate) : null],
                  ['Est. hours',   t.estimatedHours != null ? `${t.estimatedHours}h` : null],
                  ['Created',      fmtDateTime(ticket.createdAt)],
                  ['Contact',      t.contact?.name ?? null],
                ].filter(([, v]) => v).map(([label, value]) => (
                  <div key={label as string}>
                    <span className="text-xs text-muted block">{label}</span>
                    <p className="text-sm font-medium capitalize text-fg">{value}</p>
                  </div>
                ))}
              </div>

              {/* Technical details */}
              {(t.browser || t.device || t.operatingSystem || t.stepsToReproduce) && (
                <div className="mt-4 pt-4 border-t border-line-light">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">Technical details</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    {t.browser         && <div><span className="text-xs text-muted block">Browser</span><p className="text-sm font-medium text-fg">{t.browser}</p></div>}
                    {t.device          && <div><span className="text-xs text-muted block">Device</span><p className="text-sm font-medium text-fg">{t.device}</p></div>}
                    {t.operatingSystem && <div><span className="text-xs text-muted block">OS</span><p className="text-sm font-medium text-fg">{t.operatingSystem}</p></div>}
                    {t.reproducibility && <div><span className="text-xs text-muted block">Reproducibility</span><p className="text-sm font-medium capitalize text-fg">{t.reproducibility}</p></div>}
                  </div>
                  {t.stepsToReproduce && (
                    <div className="mt-3">
                      <span className="text-xs text-muted block mb-1">Steps to reproduce</span>
                      <p className="text-sm text-fg whitespace-pre-wrap font-mono text-xs bg-row-odd p-3 rounded-lg">{t.stepsToReproduce}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Custom fields */}
              {(() => {
                const pl = (t as any).productLine;
                const cf = (t as any).customFields;
                if (!pl || !cf || Object.keys(cf).length === 0) return null;
                const customFieldDefs = (pl.productLineFields ?? []) as any[];
                const entries = customFieldDefs
                  .filter((plf: any) => cf[plf.fieldDefinition?.slug] !== undefined)
                  .map((plf: any) => ({ label: plf.fieldDefinition.label, value: cf[plf.fieldDefinition.slug], type: plf.fieldDefinition.fieldType }));
                if (entries.length === 0) return null;
                return (
                  <div className="mt-4 pt-4 border-t border-line-light">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">{pl.name} fields</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {entries.map(({ label, value, type }: any) => (
                        <div key={label}>
                          <span className="text-xs text-muted block">{label}</span>
                          <p className="text-sm font-medium text-fg">
                            {type === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Tags */}
              {t.tags?.length > 0 && (
                <div className="mt-4 flex items-center gap-2 flex-wrap">
                  <Tag className="w-3.5 h-3.5 text-muted" />
                  {t.tags.map((tag: string) => (
                    <span key={tag} className="badge bg-row-odd text-muted text-xs">{tag}</span>
                  ))}
                </div>
              )}

              {/* Resolution */}
              {(t.resolution || t.rootCause || t.closedReason) && (
                <div className="mt-4 pt-4 border-t border-line-light space-y-3">
                  {t.resolution && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-ok block mb-1">Resolution</span>
                      <p className="text-sm text-fg whitespace-pre-wrap">{t.resolution}</p>
                    </div>
                  )}
                  {t.rootCause && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted block mb-1">Root cause</span>
                      <p className="text-sm text-fg whitespace-pre-wrap">{t.rootCause}</p>
                    </div>
                  )}
                  {t.closedReason && (
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted block mb-1">Closed reason</span>
                      <p className="text-sm text-fg whitespace-pre-wrap">{t.closedReason}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Related tickets — staff always see the section (with the
                  link/find-similar entry point); others only when non-empty. */}
              {(showStaffFeatures || t.parentTicket || t.subTickets?.length > 0 || linkedTickets.length > 0) && (
                <div className="mt-4 pt-4 border-t border-line-light">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted flex items-center gap-1.5">
                      <Link2 className="w-3 h-3" /> Related tickets
                    </p>
                    {showStaffFeatures && can('edit', 'tickets') && (
                      <button
                        type="button"
                        className="btn btn-ghost text-xs py-1 px-2"
                        onClick={() => setShowLinkModal(true)}
                        title="Link tickets by number or find similar ones"
                      >
                        <Plus className="w-3 h-3" /> Link / find similar
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 text-sm">
                    {t.parentTicket && (
                      <div>
                        <span className="text-muted text-xs">Parent: </span>
                        <Link to={`/tickets/${t.parentTicket.id}`} className="text-primary hover:underline text-xs">
                          {t.parentTicket.ticketNumber} — {t.parentTicket.title}
                        </Link>
                      </div>
                    )}
                    {(t.subTickets ?? []).map((st: any) => (
                      <div key={st.id}>
                        <span className="text-muted text-xs">Sub: </span>
                        <Link to={`/tickets/${st.id}`} className="text-primary hover:underline text-xs">{st.ticketNumber} — {st.title}</Link>
                      </div>
                    ))}
                    {linkedTickets.map(lt => (
                      <div key={lt.id} className="flex items-center gap-1.5">
                        <span className="text-muted text-xs shrink-0">
                          {lt.relationType === 'auto' ? 'Auto-linked' : 'Linked'}:
                        </span>
                        <Link to={`/tickets/${lt.id}`} className="text-primary hover:underline text-xs truncate">
                          {lt.ticketNumber} — {lt.title}
                        </Link>
                        {showStaffFeatures && can('edit', 'tickets') && (
                          <button
                            type="button"
                            className="text-muted hover:text-err shrink-0"
                            onClick={() => unlinkMutation.mutate(lt.id)}
                            disabled={unlinkMutation.isPending}
                            title={`Unlink ${lt.ticketNumber}`}
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    {!t.parentTicket && !(t.subTickets?.length > 0) && linkedTickets.length === 0 && (
                      <p className="text-xs text-muted">No linked tickets yet.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Sub-ticket tasks ───────────────────────────────────────── */}
          <TaskList
            ticketId={ticketId}
            companyId={companyId}
            tasks={t.tasks ?? []}
            summary={t.taskSummary}
            myId={myId}
            isStaff={showStaffFeatures}
            isCollaborator={!!t.isCollaborator}
            linkBase="/tickets"
            emailIncludeContent={!!t.emailIncludeContent}
            onChanged={invalidateTicketQueries}
          />

          {/* ── Conversation ──────────────────────────────────────────── */}
          <div className="card overflow-hidden" style={{ padding: 0 }}>
            <div className="px-5 py-3.5 border-b border-line-light flex items-center justify-between gap-3 flex-wrap">
              <h2 className="section-title text-base">Conversation</h2>
              <span className="flex items-center gap-3">
                {thread.some(isCollapsible) && (
                  <ExpandCollapseAll onExpand={collapse.expandAll} onCollapse={collapse.collapseAll} />
                )}
                <span className="text-xs text-muted">{messages.length} message{messages.length !== 1 ? 's' : ''}, newest first</span>
              </span>
            </div>

            {/* ── Compose ─────────────────────────────────────────────── */}
            {/* Above the thread: with the newest message on top, the reply
                box sits right next to what it's answering. */}
            <div className="border-b border-line">
              {/* Tab bar */}
              <div className="flex border-b border-line-light">
                <button
                  type="button"
                  onClick={() => switchComposeTab('reply')}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    composeTab === 'reply'
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted hover:text-fg'
                  }`}
                >
                  <Send className="w-3.5 h-3.5" /> Reply to Customer
                </button>
                {showStaffFeatures && (
                  <button
                    type="button"
                    onClick={() => switchComposeTab('note')}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                      composeTab === 'note'
                        ? 'border-[color:var(--scpa-status-warning)] text-warn'
                        : 'border-transparent text-muted hover:text-fg'
                    }`}
                  >
                    <Lock className="w-3.5 h-3.5" /> Internal Note
                  </button>
                )}
              </div>

              {/* A draft picked up from the server — say so, and offer to drop it. */}
              {draft.restored && (
                <div className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-dashed border-line px-3 py-2 text-xs">
                  <span className="inline-flex items-center gap-1.5 text-muted">
                    <Pencil className="w-3 h-3" style={{ color: 'var(--scpa-status-warning)' }} />
                    Picked up your unsent {draft.restored.type === 'internal_note' ? 'internal note' : 'reply'} from{' '}
                    {agoInWords(draft.restored.updatedAt)}. Nothing has been sent.
                  </span>
                  <span className="flex items-center gap-3">
                    <button type="button" className="text-primary hover:underline" onClick={draft.dismissRestored}>
                      Keep it
                    </button>
                    <button type="button" className="text-err hover:underline" onClick={discardDraft}>
                      Discard draft
                    </button>
                  </span>
                </div>
              )}

              {/* Who else has an unsent draft on this ticket (staff only). */}
              {showStaffFeatures && (t.draftsBy?.length ?? 0) > 0 && (
                <p className="mx-4 mt-3 text-[11px] text-muted inline-flex items-center gap-1.5">
                  <Pencil className="w-3 h-3" style={{ color: 'var(--scpa-status-warning)' }} />
                  {describeDrafters(t.draftsBy)}
                </p>
              )}

              {/* Compose form */}
              <form ref={composeRef} onSubmit={handleSend} className="p-4 space-y-3">
                <textarea
                  ref={replyTextareaRef}
                  className="input resize-y text-sm"
                  style={{ minHeight: 100, ...(composeTab === 'note' ? { borderColor: 'var(--scpa-status-warning)' } : {}) }}
                  placeholder={composeTab === 'note'
                    ? 'Internal note — only visible to staff… (paste a screenshot to add it inline)'
                    : 'Type your reply to the customer… (paste a screenshot to add it inline)'}
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                />

                {/* Attachment panel. Always mounted — collapsed it renders just
                    the file chips, which keeps paste-to-attach live so a pasted
                    screenshot doesn't need "Attach" clicked first. */}
                <FileUploader
                  attachments={replyAttachments}
                  onChange={setReplyAttachments}
                  context="message"
                  compact
                  enablePaste
                  pasteScopeRef={composeRef}
                  showDropZone={showAttachPanel}
                  onFilesAdded={() => setShowAttachPanel(true)}
                  onPasteImageStart={handlePasteImageStart}
                  onPasteImageDone={handlePasteImageDone}
                />

                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="submit"
                    className={composeTab === 'note' ? 'btn btn-secondary' : 'btn btn-primary'}
                    disabled={messageMutation.isPending || !replyContent.trim() || inlineUploadPending}
                    title={inlineUploadPending ? 'Waiting for image upload to finish…' : undefined}
                  >
                    {messageMutation.isPending
                      ? (composeTab === 'note' ? 'Adding…' : 'Sending…')
                      : inlineUploadPending
                        ? 'Uploading image…'
                        : (composeTab === 'note' ? 'Add Note' : 'Send Reply')}
                  </button>

                  {/* Attach files button */}
                  <button
                    type="button"
                    onClick={() => setShowAttachPanel(v => !v)}
                    className={`btn btn-ghost flex items-center gap-1.5 ${showAttachPanel ? 'text-primary' : ''}`}
                    title="Attach files"
                  >
                    <Paperclip className="w-4 h-4" />
                    {replyAttachments.length > 0 ? `${replyAttachments.length} file${replyAttachments.length !== 1 ? 's' : ''}` : 'Attach'}
                  </button>

                  {composeTab === 'reply' && showStaffFeatures && (
                    <ReplyAIAssist ticketId={ticketId} onApply={setReplyContent} />
                  )}

                  {/* Autosave state. Silent when there's nothing to save. */}
                  {draft.saveState !== 'idle' && (
                    <span
                      className="text-[11px] text-muted inline-flex items-center gap-1"
                      title={draft.savedAt ? `Draft saved ${fmtDateTime(draft.savedAt.toISOString())}. Drafts are private until you send.` : undefined}
                    >
                      {draft.saveState === 'saving' && 'Saving draft…'}
                      {draft.saveState === 'saved' && <><Check className="w-3 h-3" /> Draft saved</>}
                      {draft.saveState === 'error' && <span className="text-err">Draft not saved, retrying as you type</span>}
                    </span>
                  )}

                  {/* Re-status and/or reassign the ticket as part of this
                      update — one send, one email per person. */}
                  {(canChangeStatus || canAssign) && (
                    <div className="ml-auto flex items-center gap-1 flex-wrap">
                      {canAssign && (
                        <QuickAssign
                          variant="compose"
                          ticketId={ticketId}
                          assignedToId={currentAssigneeId}
                          assignedToName={t.assignedTo?.name ?? null}
                          staged={composeAssignee}
                          onStage={setComposeAssignee}
                          disabled={messageMutation.isPending}
                        />
                      )}
                      {canChangeStatus && (
                        <StatusPicker
                          variant="compose"
                          statuses={statusDefs}
                          current={status}
                          staged={composeStatus}
                          onSelect={setComposeStatus}
                          onClearStaged={() => setComposeStatus(null)}
                          disabled={messageMutation.isPending}
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Cross-post: pick which linked tickets this update is also
                    posted to — all, some, or none (none by default). */}
                {showStaffFeatures && linkedTickets.length > 0 && (
                  <div className="rounded-lg border border-line-light p-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-medium text-fg flex items-center gap-1.5">
                        <Link2 className="w-3 h-3 text-muted" />
                        Also post this {composeTab === 'note' ? 'note' : 'reply'} to linked tickets
                      </span>
                      <span className="text-xs text-muted shrink-0">
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => setCrossPostIds(linkedTickets.map(lt => lt.id))}
                        >
                          All
                        </button>
                        {' · '}
                        <button
                          type="button"
                          className="text-primary hover:underline"
                          onClick={() => setCrossPostIds([])}
                        >
                          None
                        </button>
                      </span>
                    </div>
                    <div className="space-y-1">
                      {linkedTickets.map(lt => (
                        <label key={lt.id} className="flex items-center gap-2 cursor-pointer text-xs">
                          <input
                            type="checkbox"
                            className="w-3.5 h-3.5 accent-[var(--scpa-primary)]"
                            checked={crossPostIds.includes(lt.id)}
                            onChange={() => toggleCrossPost(lt.id)}
                          />
                          <span className="font-mono">{lt.ticketNumber}</span>
                          <span className="text-muted truncate">{lt.title}</span>
                        </label>
                      ))}
                    </div>
                    {crossPostIds.length > 0 && (
                      <p className="text-[11px] text-muted mt-1.5">
                        The same {composeTab === 'note' ? 'internal note' : 'reply'} is posted on each checked
                        ticket, and each ticket's own notification settings decide who hears about it.
                      </p>
                    )}
                  </div>
                )}

                {/* Per-update opt-out for the ticket's "text in emails" mode —
                    uncheck when this particular update might carry PHI. */}
                {t.emailIncludeContent && (
                  <label className="flex items-start gap-2 cursor-pointer text-xs">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 mt-0.5 accent-[var(--scpa-primary)]"
                      checked={includeUpdateContent}
                      onChange={e => setIncludeUpdateContent(e.target.checked)}
                    />
                    <span>
                      <span className="font-medium">Include this update's text in notification emails</span>
                      <span className="block text-muted mt-0.5">
                        Uncheck if this update might contain PHI/sensitive data — recipients then get the usual link-only email.
                      </span>
                    </span>
                  </label>
                )}

                {composeStatus && composeStatus !== status && (
                  <p className="text-xs text-muted">
                    {composeCategory === 'resolved' || composeCategory === 'closed'
                      ? `This ${composeTab === 'note' ? 'internal note' : 'reply'} is required — it is saved as the ${composeCategory === 'resolved' ? 'resolution' : 'reason for closing'} and the status moves to ${statusDefs.find(s => s.key === composeStatus)?.label} when you send it.`
                      : `Status moves to ${statusDefs.find(s => s.key === composeStatus)?.label} when you send this ${composeTab === 'note' ? 'note' : 'reply'}.`}
                  </p>
                )}
                {assigneeStaged && (
                  <p className="text-xs text-muted">
                    {composeAssignee!.id
                      ? `Assigned to ${composeAssignee!.name} when you send this ${composeTab === 'note' ? 'note' : 'reply'}. They get one email with the update, not a separate assignment email.`
                      : `The ticket is unassigned when you send this ${composeTab === 'note' ? 'note' : 'reply'}.`}
                  </p>
                )}
              </form>
            </div>

            {/* Messages — newest first; older ones fold to a one-line preview. */}
            <div className="divide-y divide-[color:var(--scpa-border-light)]">
              {thread.length === 0 ? (
                <EmptyState>No messages yet. Send the first reply above.</EmptyState>
              ) : (
                thread.map((msg: any) => {
                  const mid = messageId(msg);
                  return (
                    <MessageBubble
                      key={mid}
                      msg={msg}
                      expanded={collapse.isExpanded(mid)}
                      onToggle={isCollapsible(msg) ? () => collapse.toggle(mid) : undefined}
                    />
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: sidebar (full-width and collapsible below lg) ────── */}
        <aside className="w-full lg:w-72 xl:w-80 shrink-0 space-y-4">

          {/* Properties */}
          <SidebarSection title="Properties">
            <dl className="space-y-0">
              <PropRow label="Status"
                value={<StatusBadge status={status} />}
              />
              <PropRow label="Priority"
                value={<PriorityBadge priority={t.priority} />}
              />
              <PropRow label="Type"      value={typeLabel(t.type)} />
              <PropRow label="Source"    value={String(t.source ?? 'portal').replace(/_/g, ' ')} />
              <PropRow label="Impact"    value={t.impact ? String(t.impact).replace(/_/g, ' ') : null} />
              <PropRow label="Env"       value={t.environment ? String(t.environment).replace(/_/g, ' ') : null} />
              <PropRow label="Due"       value={t.dueDate ? fmtDate(t.dueDate) : null} />
              <PropRow label="Est. hrs"  value={t.estimatedHours != null ? `${t.estimatedHours}h` : null} />
              {(t.slaResponseDue || t.slaResolutionDue) && (
                <>
                  <PropRow label="SLA response"    value={t.slaResponseDue ? fmtDateTime(t.slaResponseDue) : null} />
                  <PropRow label="SLA resolution"  value={t.slaResolutionDue ? fmtDateTime(t.slaResolutionDue) : null} />
                </>
              )}
              {(t.totalTimeMinutes > 0) && (
                <PropRow label="Total time" value={`${Math.floor(t.totalTimeMinutes / 60)}h ${t.totalTimeMinutes % 60}m`} />
              )}
              <PropRow label="Last activity" value={fmtDateTime(lastActivityAt)} />
            </dl>
          </SidebarSection>

          {/* Assignment */}
          <SidebarSection title="Assignment">
            <dl className="space-y-0">
              <PropRow
                label="Assignee"
                value={
                  showStaffFeatures && can('assign', 'tickets') ? (
                    <QuickAssign
                      ticketId={ticketId}
                      assignedToId={t.assignedToId ?? t.assignedTo?.id ?? null}
                      assignedToName={t.assignedTo?.name ?? null}
                      onAssigned={invalidateTicketQueries}
                    />
                  ) : (t.assignedTo?.name ?? 'Unassigned')
                }
              />
              {t.coAssignees?.length > 0 && (
                <PropRow
                  label="Co-assignees"
                  value={
                    <span className="flex flex-wrap gap-1 justify-end">
                      {t.coAssignees.map((ca: any) => (
                        <span key={ca.userId ?? ca.user?.id} className="inline-block text-xs bg-row-odd rounded-full px-2 py-0.5">
                          {ca.user?.name ?? ca.user?.email ?? ca.userId}
                        </span>
                      ))}
                    </span>
                  }
                />
              )}
              <PropRow
                label="Team"
                value={
                  t.assignedTeam
                    ? (() => {
                        const team = (teams as any[]).find((tm: any) => tm.id === t.assignedTeam.id);
                        const names = (team?.members ?? [])
                          .map((m: any) => m.user?.name ?? m.user?.email)
                          .filter(Boolean);
                        return (
                          <span
                            title={names.length > 0 ? `Members: ${names.join(', ')}` : 'No members'}
                            className="cursor-help underline decoration-dotted underline-offset-2"
                          >
                            {t.assignedTeam.name}
                          </span>
                        );
                      })()
                    : null
                }
              />
              <PropRow label="Contact"   value={t.contact?.name ?? (t.crmContact ? `${t.crmContact.firstName} ${t.crmContact.lastName ?? ''}`.trim() : null)} />
              <PropRow label="Product line" value={(t as any).productLine?.name ?? null} />
              <PropRow label="Category"  value={t.category?.name ?? null} />
              {/* API-filed tickets are attributed to the tenant owner as a DB
                  FK — the real originator is the integration (sourceApp). */}
              <PropRow label="Created by" value={t.source === 'api' && t.sourceApp ? `${t.sourceApp} (integration)` : (t.createdBy?.name ?? null)} />
              <PropRow label="Created"   value={fmtDateTime(ticket.createdAt)} />
              {t.resolvedAt && <PropRow label="Resolved" value={fmtDateTime(t.resolvedAt)} />}
              {t.closedAt   && <PropRow label="Closed"   value={fmtDateTime(t.closedAt)} />}
              {t.firstResponseAt && <PropRow label="First response" value={fmtDateTime(t.firstResponseAt)} />}
            </dl>
          </SidebarSection>

          {/* Collaborators — outside team members attached to this ticket. */}
          <SidebarSection title="Collaborators">
            <CollaboratorsPanel
              ticketId={ticketId}
              collaborators={t.collaborators ?? []}
              invites={t.collaboratorInvites ?? []}
              canManage={showStaffFeatures}
              organizationName={t.crmOrganization?.name ?? null}
              onChanged={invalidateTicketQueries}
            />
          </SidebarSection>

          {/* Satisfaction */}
          {t.satisfactionRating && (
            <SidebarSection title="Satisfaction">
              <p className="text-sm font-medium capitalize text-fg">{String(t.satisfactionRating).replace(/_/g, ' ')}</p>
              {t.satisfactionComment && (
                <p className="text-xs text-muted italic mt-1">"{t.satisfactionComment}"</p>
              )}
            </SidebarSection>
          )}

          {/* Notifications (staff) */}
          {showStaffFeatures && (
            <SidebarSection title="Notifications">
              <button
                type="button"
                onClick={() => notifyMutation.mutate(!t.notifyCustomer)}
                disabled={notifyMutation.isPending}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                style={
                  t.notifyCustomer
                    ? { background: 'var(--scpa-primary-muted)', color: 'var(--scpa-status-ok)' }
                    : { background: 'var(--scpa-row-odd)', color: 'var(--scpa-text-muted)' }
                }
                title={t.notifyCustomer ? 'Click to mute' : 'Click to enable customer emails'}
              >
                <span className="flex items-center gap-2">
                  {t.notifyCustomer ? <BellRing className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  {t.notifyCustomer ? 'Customer notifications on' : 'Customer notifications muted'}
                </span>
              </button>
              <p className="text-[11px] text-muted mt-1.5 leading-relaxed">
                {t.notifyCustomer
                  ? 'Reporter and CCs receive brief emails (with a magic link) on replies and status changes. Internal notes are never sent.'
                  : 'No customer emails will be sent on this ticket. You can still reply internally.'}
              </p>
              {/* Muting is otherwise unexplained — tickets filed through the
                  integration API are muted on arrival by design, which is the
                  case people actually hit. Anything else was a person, and the
                  Activity log records who. */}
              {!t.notifyCustomer && (
                <p className="text-[11px] mt-1.5 leading-relaxed" style={{ color: 'var(--scpa-status-warning)' }}>
                  {t.source === 'api'
                    ? `Muted on arrival — ${t.sourceApp || 'an integration'} filed this through the API, which never auto-emails the reporter.`
                    : 'Muted manually on this ticket — see Activity for who changed it and when.'}
                </p>
              )}

              {/* Creator opt-in — the creator hears about updates like an assignee. */}
              <button
                type="button"
                onClick={() => creatorNotifyMutation.mutate(!t.notifyCreator)}
                disabled={creatorNotifyMutation.isPending}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mt-2"
                style={
                  t.notifyCreator
                    ? { background: 'var(--scpa-primary-muted)', color: 'var(--scpa-status-ok)' }
                    : { background: 'var(--scpa-row-odd)', color: 'var(--scpa-text-muted)' }
                }
                title={t.notifyCreator ? 'Click to stop notifying the creator' : 'Click to notify the creator of updates'}
              >
                <span className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4" />
                  {t.notifyCreator ? 'Creator notifications on' : 'Creator notifications off'}
                </span>
              </button>
              <p className="text-[11px] text-muted mt-1.5 leading-relaxed">
                {t.notifyCreator
                  ? `${t.createdBy?.name || 'The creator'} gets an email on every reply, note, and status change (never their own actions).`
                  : 'The creator is only emailed when they are also assigned. Turn on to follow this ticket by email.'}
              </p>

              {/* Update text in emails — per-ticket PHI-sensitive opt-in. */}
              <button
                type="button"
                onClick={() => includeContentMutation.mutate(!t.emailIncludeContent)}
                disabled={includeContentMutation.isPending}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors mt-2"
                style={
                  t.emailIncludeContent
                    ? { background: 'var(--scpa-primary-muted)', color: 'var(--scpa-status-warning)' }
                    : { background: 'var(--scpa-row-odd)', color: 'var(--scpa-text-muted)' }
                }
                title={t.emailIncludeContent ? 'Click to send link-only emails again' : 'Click to include update text in emails'}
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t.emailIncludeContent ? 'Update text in emails' : 'Link-only emails'}
                </span>
              </button>
              <p className="text-[11px] text-muted mt-1.5 leading-relaxed" style={t.emailIncludeContent ? { color: 'var(--scpa-status-warning)' } : undefined}>
                {t.emailIncludeContent
                  ? 'Notification emails carry the actual update text — only keep this on if this ticket can never contain PHI/HIPAA data. Each update can still be excluded from the compose box.'
                  : 'Notification emails are content-free (link only) — the safe default for PHI.'}
              </p>

              <div className="mt-4 pt-4 border-t border-line-light">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-2 flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Recipients
                </p>

                {/* Primary reporter (read-only, click to copy the address) */}
                <div className="text-xs space-y-1 mb-3">
                  {primaryTo ? (
                    <CopyableEmail recipient={primaryTo} label="TO" />
                  ) : (
                    <p className="text-muted italic">No primary recipient — emails will not be sent.</p>
                  )}
                </div>

                {/* CCs */}
                {t.ccRecipients?.length > 0 && (
                  <ul className="space-y-1 mb-3">
                    {t.ccRecipients.map((cc: any) => {
                      const recipient = ccRecipient(cc);
                      if (!recipient) return null;
                      return (
                        <li key={cc.id} className="text-xs bg-row-odd rounded-md px-2 py-1.5">
                          <CopyableEmail
                            recipient={recipient}
                            label="CC"
                            labelClassName="bg-row-even text-muted"
                            action={
                              <button
                                type="button"
                                onClick={() => removeCcMutation.mutate(cc.id)}
                                disabled={removeCcMutation.isPending}
                                className="text-muted hover:text-err shrink-0 mt-0.5"
                                title="Remove"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            }
                          />
                        </li>
                      );
                    })}
                  </ul>
                )}

                {can('edit', 'tickets') && (
                  <div className="space-y-1.5">
                    <input
                      type="email"
                      className="input text-xs py-1.5"
                      placeholder="email@example.com"
                      value={ccEmailInput}
                      onChange={e => setCcEmailInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ccEmailInput.trim())) {
                            addCcMutation.mutate({ email: ccEmailInput.trim().toLowerCase(), name: ccNameInput.trim() || undefined });
                          } else {
                            toast.error('Enter a valid email');
                          }
                        }
                      }}
                    />
                    <div className="flex gap-1">
                      <input
                        type="text"
                        className="input text-xs py-1.5 flex-1"
                        placeholder="Name (optional)"
                        value={ccNameInput}
                        onChange={e => setCcNameInput(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost text-xs py-1.5"
                        disabled={addCcMutation.isPending}
                        onClick={() => {
                          const email = ccEmailInput.trim().toLowerCase();
                          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                            toast.error('Enter a valid email');
                            return;
                          }
                          addCcMutation.mutate({ email, name: ccNameInput.trim() || undefined });
                        }}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </SidebarSection>
          )}

          {/* Time entries (staff) */}
          {showStaffFeatures && can('view', 'timeEntries') && (
            <SidebarSection title="Time tracking">
              <TimeEntryForm ticketId={ticketId} />
              <div className="mt-4 pt-4 border-t border-line-light">
                <TimeEntryList ticketId={ticketId} />
              </div>
            </SidebarSection>
          )}

          {/* Activity (staff) */}
          {showStaffFeatures && (
            <SidebarSection title="Activity">
              <ActivityLog ticketId={ticketId} />
            </SidebarSection>
          )}

          {/* AI (staff) */}
          {showStaffFeatures && (
            <AIPanel
              ticketId={ticketId}
              ticketTitle={ticket.title}
              ticketDescription={ticket.description}
              onApplySuggestion={(text) => {
                setComposeTab('reply');
                setReplyContent(text);
              }}
            />
          )}
        </aside>
      </div>

      {showEditModal && can('edit', 'tickets') && (
        <TicketEditModal
          ticket={ticket as Ticket}
          onClose={() => setShowEditModal(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['ticket', id] })}
        />
      )}

      {showShareModal && showStaffFeatures && (
        <ShareTicketModal
          ticketId={ticketId}
          ticketNumber={t.ticketNumber}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showAIPromptModal && showStaffFeatures && (
        <AIPromptModal
          ticketId={ticketId}
          ticketNumber={t.ticketNumber}
          onClose={() => setShowAIPromptModal(false)}
        />
      )}

      {/* Mounted conditionally so its checkbox/agent state resets each use. */}
      {showSendToClaude && showStaffFeatures && (
        <SendToClaudeModal
          ticketId={ticketId}
          ticket={t}
          messages={messages as any[]}
          onClose={() => setShowSendToClaude(false)}
        />
      )}

      {/* Mounted conditionally so its lookup/selection state resets each use. */}
      {showLinkModal && showStaffFeatures && (
        <LinkTicketsModal
          open
          onClose={() => setShowLinkModal(false)}
          ticketId={ticketId}
          linkedIds={[
            ...linkedTickets.map(lt => lt.id),
            ...(t.parentTicket ? [t.parentTicket.id] : []),
            ...((t.subTickets ?? []).map((st: any) => st.id)),
          ]}
        />
      )}

      <ChangeStatusModal
        open={showStatusModal}
        onClose={closeStatusModal}
        statuses={statusDefs}
        currentStatus={status}
        currentResolution={t.resolution ?? undefined}
        initialStatus={pendingStatus}
        pending={statusMutation.isPending}
        onSubmit={(newStatus, extra) => statusMutation.mutate({ status: newStatus, extra })}
      />
    </div>
    </ImageLightboxProvider>
  );
}
